import { useState } from 'react';
import type { ModalConfigState } from '../types';

// Wraps the generic alert/confirm/prompt ModalConfigState that used to be
// set directly (and closed via an identical `setModalConfig(prev => ({
// ...prev, isOpen: false }))` inline closure) at ~14 call sites in App.tsx.
export interface UseModalConfigResult {
  modalConfig: ModalConfigState;
  showAlert: (title: string, message: string) => void;
  showConfirm: (title: string, message: string, onConfirm: () => void | Promise<void>) => void;
  showPrompt: (
    title: string,
    promptFields: NonNullable<ModalConfigState['promptFields']>,
    onConfirm: (values?: Record<string, string>) => void | Promise<void>
  ) => void;
  close: () => void;
}

const INITIAL_MODAL_CONFIG: ModalConfigState = {
  isOpen: false,
  title: '',
  type: 'alert',
  onConfirm: () => undefined,
};

export function useModalConfig(): UseModalConfigResult {
  const [modalConfig, setModalConfig] = useState<ModalConfigState>(INITIAL_MODAL_CONFIG);

  const close = (): void => setModalConfig(prev => ({ ...prev, isOpen: false }));

  const showAlert = (title: string, message: string): void => {
    setModalConfig({
      isOpen: true,
      title,
      message,
      type: 'alert',
      onConfirm: close,
    });
  };

  const showConfirm = (title: string, message: string, onConfirm: () => void | Promise<void>): void => {
    setModalConfig({
      isOpen: true,
      title,
      message,
      type: 'confirm',
      onConfirm: async () => {
        await onConfirm();
        close();
      },
    });
  };

  const showPrompt = (
    title: string,
    promptFields: NonNullable<ModalConfigState['promptFields']>,
    onConfirm: (values?: Record<string, string>) => void | Promise<void>
  ): void => {
    setModalConfig({
      isOpen: true,
      title,
      type: 'prompt',
      promptFields,
      onConfirm: async (values) => {
        await onConfirm(values);
        close();
      },
    });
  };

  return { modalConfig, showAlert, showConfirm, showPrompt, close };
}
